import apiClient from './api/client';
import ENDPOINTS from './api/endpoints';

/**
 * KOT Service
 * Handles all Kitchen Order Ticket related API calls
 */

export const kotService = {
  /**
   * Send new KOT to kitchen
   * @param {Object} kotData - KOT data including items, table, etc.
   * @returns {Promise} KOT creation response
   */
  sendKOT: async (kotData) => {
    try {
      const response = await apiClient.post(ENDPOINTS.KOT.CREATE, kotData);
      return response;
    } catch (error) {
      console.error('Failed to send KOT:', error);
      throw error;
    }
  },

  /**
   * Get all KOTs
   * @returns {Promise} List of all KOTs
   */
  getAllKOTs: async () => {
    try {
      const response = await apiClient.get(ENDPOINTS.KOT.GET_ALL);
      return response;
    } catch (error) {
      console.error('Failed to fetch KOTs:', error);
      throw error;
    }
  },

  /**
   * Get KOT by ID
   * @param {string} kotId - KOT ID
   * @returns {Promise} KOT details
   */
  getKOTById: async (kotId) => {
    try {
      const response = await apiClient.get(ENDPOINTS.KOT.GET_BY_ID(kotId));
      return response;
    } catch (error) {
      console.error(`Failed to fetch KOT ${kotId}:`, error);
      throw error;
    }
  },

  /**
   * Update KOT status
   * @param {string} kotId - KOT ID
   * @param {string} status - New status (sent, preparing, ready, served)
   * @returns {Promise} Updated KOT
   */
  updateKOTStatus: async (kotId, status) => {
    try {
      const response = await apiClient.patch(ENDPOINTS.KOT.UPDATE_STATUS(kotId), { status });
      return response;
    } catch (error) {
      console.error(`Failed to update KOT ${kotId} status:`, error);
      throw error;
    }
  },

  /**
   * Cancel KOT
   * @param {string} kotId - KOT ID (Order ID)
   * @param {string} reason - Cancellation reason
   * @returns {Promise} Cancellation confirmation
   */
  cancelKOT: async (kotId, reason) => {
    try {
      const response = await apiClient.put(ENDPOINTS.KOT.CANCEL(kotId), { reason });
      return response;
    } catch (error) {
      console.error(`Failed to cancel KOT ${kotId}:`, error);
      throw error;
    }
  },

  /**
   * Get KOTs by table ID
   * @param {string} tableId - Table ID
   * @returns {Promise} List of KOTs for the table
   */
  getKOTsByTable: async (tableId) => {
    try {
      const response = await apiClient.get(ENDPOINTS.KOT.GET_BY_TABLE(tableId));
      return response;
    } catch (error) {
      console.error(`Failed to fetch KOTs for table ${tableId}:`, error);
      throw error;
    }
  },

  /**
   * Get active orders by table ID and split
   * @param {number|string} tableId - Table ID
   * @param {string} tableSplit - Table split (e.g., 'A', 'B', 'C')
   * @returns {Promise} List of active orders for the table split
   */
  getActiveOrders: async (tableId, tableSplit) => {
    try {
      const response = await apiClient.get(ENDPOINTS.KOT.GET_ACTIVE_BY_TABLE_SPLIT(tableId, tableSplit));
      return response;
    } catch (error) {
      console.error(`Failed to fetch active orders for table ${tableId} split ${tableSplit}:`, error);
      throw error;
    }
  },

  /**
   * Get recent orders with optional filters
   * @param {Object} params - Query parameters { tableId, limit, days }
   * @returns {Promise} List of recent orders
   */
  getRecentOrders: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (params.tableId) queryParams.append('tableId', params.tableId);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.days) queryParams.append('days', params.days);

      const endpoint = `${ENDPOINTS.KOT.GET_RECENT}?${queryParams.toString()}`;
      const response = await apiClient.get(endpoint);
      return response;
    } catch (error) {
      console.error('Failed to fetch recent orders:', error);
      throw error;
    }
  },

  /**
   * Cancel all orders for a table split
   * @param {number|string} tableId - Table ID
   * @param {string} tableSplit - Table split name (e.g., 'A', 'B', 'C')
   * @returns {Promise} Cancellation confirmation
   */
  cancelOrdersByTableSplit: async (tableId, tableSplit) => {
    try {
      const response = await apiClient.put(ENDPOINTS.KOT.CANCEL_BY_TABLE_SPLIT(tableId, tableSplit));
      return response;
    } catch (error) {
      console.error(`Failed to cancel orders for table ${tableId} split ${tableSplit}:`, error);
      throw error;
    }
  },
};

export default kotService;
